// Core packages
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// Third party packages
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

// Custom packages
import { MatDialog } from '@angular/material';
import { UsersService } from 'src/app/modules/users/users.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ConfigService } from 'src/app/shared/services/config.service';
import { HelperService } from 'src/app/shared/services/helper.service';
import { ThemeService } from 'src/app/shared/services/theme.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  ownerEmail = this.configService.ownerPublicEmail;
  token: string;

  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant('auth.resetPassword.form.submitButtonText'),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: 'primary',
    spinnerColor: 'primary',
    fullWidth: false,
    disabled: false,
    mode: 'indeterminate',
    buttonIcon: {
      fontSet: 'fal',
      fontIcon: 'fa-arrow-right',
      inline: true,
    },
  };

  constructor(
    private usersService: UsersService,
    private helperService: HelperService,
    private toastrService: ToastrService,
    private configService: ConfigService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit() {
    // Subscribe to current lang
    this.lang = this.translateService.currentLang;
    if (this.activatedRoute.params) {
      this.subscriptions.push(
        this.activatedRoute.params.subscribe(
          res => {
            if (res) {
              if (res.lang) {
                this.lang = res.lang;
              }
              if (res.token) {
                this.token = res.token;
              }
              this.handleRouteParamsChange();
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        )
      );
    }
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscription
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Init component
   */
  handleRouteParamsChange(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('auth.resetPassword.pageTitle'));

    // Prevent form fields to be rendered twice on language change
    this.fields = [];

    // Initialize form
    this.initForm();

    // Set correct button text based on current language
    this.formBtnOptions.text = this.translateService.instant('auth.forgotPassword.form.submitButtonText');

    // No token in url, redirect to login
    if (!this.token) {
      this.toastrService.error(this.translateService.instant('common.errors.invalid_recover_link'));
      setTimeout(() => {
        this.router.navigate([this.lang, 'auth', 'login']);
        return;
      }, 1500);
    }
  }

  /**
   * Init form
   */
  initForm(): void {
    // Set model
    this.model = {};

    // Set fields
    this.fields = [
      ...this.fields,
      {
        // see https://stackblitz.com/angular/reveaanjebg
        key: 'newPassword',
        validators: {
          fieldMatch: {
            expression: (control) => {
              const value = control.value;

              return value.confirmPassword === value.password
                // avoid displaying the message error when values are empty
                || (!value.confirmPassword || !value.password);
            },
            message: this.translateService.instant('dialogs.changePassword.password_not_matching'),
            errorPath: 'confirmPassword',
          },
        },
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'email',
            templateOptions: {
              label: this.translateService.instant('auth.resetPassword.form.email.label'),
              placeholder: this.translateService.instant('auth.resetPassword.form.email.placeholder'),
              required: true,
              minLength: 5,
              maxLength: 320,
              type: 'email',
              pattern: this.configService.emailPattern,
            },
          },
          {
            className: 'col-10',
            type: 'input',
            key: 'password',
            templateOptions: {
              label: this.translateService.instant('dialogs.changePassword.password'),
              placeholder: this.translateService.instant('dialogs.changePassword.password'),
              required: true,
              minLength: 5,
              maxLength: 100,
              type: 'password',
            },
          },
          {
            className: 'col-2',
            type: 'button',
            key: 'showHidePassword',
            templateOptions: {
              color: "accent",
              tooltip: "tooltip",
              icon: {
                fontSet: "fas",
                fontIcon: "fa-eye",
              },
              onClick: ($event) =>
                this.showHidePassword(),
            },
          },
          {
            className: 'col-10',
            type: 'input',
            key: 'confirmPassword',
            templateOptions: {
              label: this.translateService.instant('dialogs.changePassword.confirmPassword'),
              placeholder: this.translateService.instant('dialogs.changePassword.confirmPassword'),
              required: true,
              type: 'password',
            },
          },
          {
            className: 'col-2',
            type: 'button',
            key: 'showHideConfirmPassword',
            templateOptions: {
              color: "accent",
              tooltip: "tooltip",
              icon: {
                fontSet: "fas",
                fontIcon: "fa-eye",
              },
              onClick: ($event) =>
                this.showHideConfirmPassword(),
            },
          }
        ],
      },
    ];
  }

  /**
   * Handle show and hide passwword field
   *
   * @since 1.0.0
   */
   showHidePassword(): void {
    if (this.fields && Array.isArray(this.fields)) {
      this.fields.forEach((el: any) => {
        if (el.fieldGroup && Array.isArray(el.fieldGroup)) {
          let index = el.fieldGroup.findIndex((subEl: any) => {
            return subEl.key === 'password';
          });
          if (
            index > -1 && 
            el.fieldGroup[index].templateOptions &&
            el.fieldGroup[index].templateOptions.type
          ) {
            if (el.fieldGroup[index].templateOptions.type === 'text') {
              el.fieldGroup[index].templateOptions.type = 'password';
            } else {
              el.fieldGroup[index].templateOptions.type = 'text';
            }
          };
          index = el.fieldGroup.findIndex((subEl: any) => {
            return subEl.key === 'showHidePassword';
          });
          if (
            index > -1 &&
            el.fieldGroup[index].templateOptions &&
            el.fieldGroup[index].templateOptions.icon &&
            el.fieldGroup[index].templateOptions.icon.fontIcon
          ) {
            if (el.fieldGroup[index].templateOptions.icon.fontIcon === 'fa-eye') {
              el.fieldGroup[index].templateOptions.icon.fontIcon = 'fa-eye-slash';
            } else {
              el.fieldGroup[index].templateOptions.icon.fontIcon = 'fa-eye';
            }
          };
        }
      });
    }
  }

  /**
   * Handle show and hide passwword field
   *
   * @since 1.0.0
   */
   showHideConfirmPassword(): void {
    if (this.fields && Array.isArray(this.fields)) {
      this.fields.forEach((el: any) => {
        if (el.fieldGroup && Array.isArray(el.fieldGroup)) {
          let index = el.fieldGroup.findIndex((subEl: any) => {
            return subEl.key === 'confirmPassword';
          });
          if (
            index > -1 && 
            el.fieldGroup[index].templateOptions &&
            el.fieldGroup[index].templateOptions.type
          ) {
            if (el.fieldGroup[index].templateOptions.type === 'text') {
              el.fieldGroup[index].templateOptions.type = 'password';
            } else {
              el.fieldGroup[index].templateOptions.type = 'text';
            }
          };
          index = el.fieldGroup.findIndex((subEl: any) => {
            return subEl.key === 'showHideConfirmPassword';
          });
          if (
            index > -1 &&
            el.fieldGroup[index].templateOptions &&
            el.fieldGroup[index].templateOptions.icon &&
            el.fieldGroup[index].templateOptions.icon.fontIcon
          ) {
            if (el.fieldGroup[index].templateOptions.icon.fontIcon === 'fa-eye') {
              el.fieldGroup[index].templateOptions.icon.fontIcon = 'fa-eye-slash';
            } else {
              el.fieldGroup[index].templateOptions.icon.fontIcon = 'fa-eye';
            }
          };
        }
      });
    }
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant('common.errors.form_is_invalid'),
        this.translateService.instant('common.words.error')
      );
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    this.subscriptions.push(
      this.usersService.resetPassword(model.newPassword.password, model.newPassword.email, this.token)
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                  title: this.translateService.instant('common.words.operation_successful'),
                  message: this.translateService.instant('dialogs.resetPassword.password_reset_successfully'),
                  btnOkText: this.translateService.instant('dialogs.resetPassword.login'),
                  btnCancelText: this.translateService.instant('common.cta.close'),
                },
                width: '500px',
                disableClose: true,

              });
              // Subscribe to dialog result (only for 1 emit thanks to "take()")
              dialogRef.beforeClosed()
                .pipe(
                  take(1)
                )
                .subscribe(
                  res2 => {
                    if (res2) {
                      // User hit confirm button. We can redirect him
                      this.router.navigate([this.lang, 'auth', 'login']);
                    }
                  }
                );
              return;
            }
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
}
